import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, jsonb, boolean, uuid, integer, numeric, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: uuid("id").primaryKey().defaultRandom(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  isAdmin: boolean("is_admin").notNull().default(false),
  department: text("department").notNull().default("Sales"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  profilePictureUrl: text("profile_picture_url"),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export const insertAdminUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  isAdmin: true,
  department: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertAdminUser = z.infer<typeof insertAdminUserSchema>;
export type User = typeof users.$inferSelect;

export const conversations = pgTable("conversations", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().references(() => users.id),
  title: text("title").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertConversationSchema = createInsertSchema(conversations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Conversation = typeof conversations.$inferSelect;

export const messages = pgTable("messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  conversationId: uuid("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  role: text("role").notNull(), // "user" or "assistant"
  content: text("content").notNull(),
  sources: jsonb("sources"), // Array of {documentId, filename, relevance} objects
  cachedResponseId: uuid("cached_response_id").references(() => cachedResponses.id), // Link to cached response if applicable
  isExceptionResponse: boolean("is_exception_response").notNull().default(false), // Track if this is an exception search response
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});

export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;

export const documents = pgTable("documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  filename: text("filename").notNull(),
  content: text("content").notNull(),
  documentType: text("document_type").notNull().default("guideline"), // "guideline" or "exception"
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`), // Product tags: Conventional, FHA, DSCR, etc.
  openaiFileId: text("openai_file_id"), // OpenAI file ID for Assistants API
  uploadedBy: uuid("uploaded_by").notNull().references(() => users.id),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
});

export const insertDocumentSchema = createInsertSchema(documents).pick({
  filename: true,
  content: true,
  documentType: true,
  tags: true,
  uploadedBy: true,
});

export type InsertDocument = z.infer<typeof insertDocumentSchema>;
export type Document = typeof documents.$inferSelect;

export const documentChunks = pgTable("document_chunks", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
  chunkText: text("chunk_text").notNull(),
  embedding: text("embedding"), // JSON string of float array (nullable for Claude-only search)
  chunkIndex: text("chunk_index").notNull(), // Position of chunk in document
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentChunkSchema = createInsertSchema(documentChunks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentChunk = z.infer<typeof insertDocumentChunkSchema>;
export type DocumentChunk = typeof documentChunks.$inferSelect;

export const settings = pgTable("settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
});

export const insertSettingSchema = createInsertSchema(settings);

export type InsertSetting = z.infer<typeof insertSettingSchema>;
export type Setting = typeof settings.$inferSelect;

export const cachedResponses = pgTable("cached_responses", {
  id: uuid("id").primaryKey().defaultRandom(),
  originalQuery: text("original_query").notNull(),
  refinedQuery: text("refined_query").notNull(),
  embedding: text("embedding"), // JSON string of embedding vector for semantic similarity
  agent: text("agent"), // Which area: DSCR, FHA, Exceptions, etc.
  answer: text("answer").notNull(),
  sources: jsonb("sources"),
  suggestedQuestions: jsonb("suggested_questions"),
  votes: jsonb("votes").notNull().default(sql`'{"up": 0, "down": 0}'::jsonb`),
  comments: jsonb("comments").notNull().default(sql`'[]'::jsonb`), // Array of {text, userId, username, createdAt}
  approved: boolean("approved").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCachedResponseSchema = createInsertSchema(cachedResponses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCachedResponse = z.infer<typeof insertCachedResponseSchema>;
export type CachedResponse = typeof cachedResponses.$inferSelect;

export const messageRatings = pgTable("message_ratings", {
  id: uuid("id").primaryKey().defaultRandom(),
  messageId: uuid("message_id").notNull().references(() => messages.id, { onDelete: "cascade" }),
  userId: uuid("user_id").notNull().references(() => users.id),
  vote: text("vote").notNull(), // "up" or "down"
  comment: text("comment"), // Optional comment explaining the rating
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertMessageRatingSchema = createInsertSchema(messageRatings).omit({
  id: true,
  createdAt: true,
});

export type InsertMessageRating = z.infer<typeof insertMessageRatingSchema>;
export type MessageRating = typeof messageRatings.$inferSelect;

export const featureRequests = pgTable("feature_requests", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category"),
  status: text("status").notNull().default("open"),
  priority: text("priority").default("medium"), // Priority level: high, medium, low
  buildStatus: text("build_status").default("planning"), // Build status: planning, building, needs-review, approval, completed, on-hold
  upvoteCount: integer("upvote_count").notNull().default(0),
  downvoteCount: integer("downvote_count").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const featureVotes = pgTable("feature_votes", {
  id: uuid("id").primaryKey().defaultRandom(),
  featureRequestId: uuid("feature_request_id").notNull().references(() => featureRequests.id, { onDelete: "cascade" }),
  userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  voteType: text("vote_type").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const userActivityScores = pgTable("user_activity_scores", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
  totalPoints: integer("total_points").notNull().default(0),
  messagesSent: integer("messages_sent").notNull().default(0),
  conversationsCreated: integer("conversations_created").notNull().default(0),
  helpfulVotesReceived: integer("helpful_votes_received").notNull().default(0),
  votesGiven: integer("votes_given").notNull().default(0),
  featuresSuggested: integer("features_suggested").notNull().default(0),
  featuresVoted: integer("features_voted").notNull().default(0),
  tier: text("tier").notNull().default("Bronze"),
  lastActivityAt: timestamp("last_activity_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type FeatureRequest = typeof featureRequests.$inferSelect;
export type FeatureVote = typeof featureVotes.$inferSelect;
export type UserActivityScore = typeof userActivityScores.$inferSelect;
